import { api, apiErrorMessage } from '@bemor/web-kit';
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    DateTime,
    DoneIcon,
    FailedIcon,
    NeutralIcon,
    PendingIcon,
    PublicId,
    RetryIcon,
    StatusBadge,
} from '@bemor/web-kit/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import type { Tenant } from '@/api/types';
import { useWrite } from '@/lib/queries';
import { ADMIN_STATUSES } from '@/lib/statuses';

type Ledger = NonNullable<Tenant['ledger']>;
type Step = Ledger['steps'][number];

/** How often the card reads the tenant again while a run holds it (ms). */
const POLL_MS = 3000;
/** After Provision / Retry, how long the card waits for the queued run to finish (ms). */
const AWAIT_RUN_MS = 60_000;

/** Nexroll's `formatElapsed`: a step's run time as "42s", "3m" or "3m 5s". */
export function formatElapsed(seconds: number): string {
    if (seconds < 60) return `${seconds}s`;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return s === 0 ? `${m}m` : `${m}m ${s}s`;
}

function stepSeconds(step: Step): number | null {
    const end = step.completed_at ?? step.failed_at;
    if (!step.started_at || !end) return null;
    const diff =
        (new Date(end).getTime() - new Date(step.started_at).getTime()) / 1000;
    return diff > 0 ? Math.round(diff) : null;
}

/** Nexroll's `StepRow`: the step's mark, name, run time and when it ended; the error under a failed step. */
export function StepRow({ step }: { step: Step }) {
    const Icon =
        step.status === 'done'
            ? DoneIcon
            : step.status === 'failed'
              ? FailedIcon
              : step.status === 'running'
                ? PendingIcon
                : NeutralIcon;
    const tone =
        step.status === 'done'
            ? 'text-success'
            : step.status === 'failed'
              ? 'text-destructive'
              : step.status === 'running'
                ? 'text-info'
                : 'text-fg-3';
    const seconds = step.status === 'running' ? null : stepSeconds(step);
    return (
        <li
            className="space-y-1 py-2"
            data-testid="ledger-step"
            data-step={step.code}
            data-status={step.status}
        >
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span className="flex min-w-0 items-center gap-2">
                    <Icon className={`size-4 shrink-0 ${tone}`} aria-hidden />
                    <span>{step.name}</span>
                </span>
                <span className="text-muted-foreground flex items-center gap-3 text-xs">
                    {seconds !== null ? (
                        <span>{formatElapsed(seconds)}</span>
                    ) : null}
                    <DateTime
                        value={step.completed_at ?? step.failed_at}
                        empty="—"
                    />
                    <StatusBadge
                        status={step.status}
                        domain={ADMIN_STATUSES.ledgerStep}
                    />
                </span>
            </div>
            {step.status === 'failed' && step.error ? (
                <p
                    className="text-destructive ml-6 text-xs break-words"
                    data-testid="ledger-step-error"
                >
                    {step.error}
                </p>
            ) : null}
        </li>
    );
}

/**
 * BM-145: the tenant's Ledger provisioning on the platform host, copied from Nexroll's provisioning card: the three
 * steps (tenant, key, webhook) with their state and error, the Ledger tenant, environment and key count. One button
 * runs the job from the first unfinished step: "Provision" when nothing ran yet, "Retry" when a step failed; it is off
 * while a run holds the tenant. "Re-queue dead events" puts the tenant's dead-lettered Ledger deliveries back.
 */
export function LedgerSection({ tenant }: { tenant: Tenant }) {
    const ledger = tenant.ledger;
    const client = useQueryClient();
    const [error, setError] = useState<string | null>(null);
    const [requeued, setRequeued] = useState<number | null>(null);
    // QA 2026-11-02: the request answers 202 before the job runs; until the run ends the card keeps reading
    const [requestedAt, setRequestedAt] = useState<number | null>(null);
    const inv = ['/staff/tenants'];
    const provision = useWrite(
        async () =>
            (
                await api().post<{ queued: boolean }>(
                    `/staff/tenants/${tenant.id}/ledger/provision`,
                )
            ).data,
        inv,
    );
    const requeue = useWrite(
        async () =>
            (
                await api().post<{ requeued: number }>(
                    `/staff/tenants/${tenant.id}/ledger/requeue`,
                )
            ).data,
        inv,
    );
    const running = ledger?.status === 'running';
    const steps = ledger?.steps ?? [];
    const allDone = steps.length > 0 && steps.every((s) => s.status === 'done');
    // the queued run ended: all done, or a step failed after the request (a little clock slack between api and browser)
    const ended =
        requestedAt !== null &&
        (allDone ||
            steps.some(
                (s) =>
                    s.status === 'failed' &&
                    s.failed_at !== null &&
                    new Date(s.failed_at).getTime() >= requestedAt - 5000,
            ));
    const awaiting = requestedAt !== null && !ended;
    useEffect(() => {
        if (!awaiting || requestedAt === null) return;
        const timer = window.setTimeout(
            () => setRequestedAt(null),
            Math.max(0, requestedAt + AWAIT_RUN_MS - Date.now()),
        );
        return () => window.clearTimeout(timer);
    }, [awaiting, requestedAt]);
    // read the tenant again while a run holds it or one was just asked for, so the steps move on their own
    const polling = running || awaiting;
    useEffect(() => {
        if (!polling) return;
        const timer = window.setInterval(() => {
            void client.invalidateQueries({
                predicate: (q) =>
                    JSON.stringify(q.queryKey).includes(
                        `/staff/tenants/${tenant.id}`,
                    ),
            });
        }, POLL_MS);
        return () => window.clearInterval(timer);
    }, [polling, client, tenant.id]);

    if (!ledger) return null;
    const failed = steps.find((s) => s.status === 'failed');
    const fresh = steps.every((s) => s.status === 'pending');
    const title =
        running || awaiting
            ? 'Provisioning'
            : failed
              ? 'Provisioning failed'
              : allDone
                ? 'Provisioned'
                : 'Not provisioned';
    const onError = (e: unknown) => setError(apiErrorMessage(e));
    return (
        <Card data-testid="tenant-ledger">
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-sm font-medium">
                    Ledger · {title}
                </CardTitle>
                <div className="flex flex-wrap gap-2">
                    {!allDone ? (
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={
                                running || awaiting || provision.isPending
                            }
                            data-testid="ledger-provision"
                            onClick={() => {
                                setError(null);
                                provision.mutate(undefined, {
                                    onError,
                                    onSuccess: () => setRequestedAt(Date.now()),
                                });
                            }}
                        >
                            <RetryIcon className="size-3" aria-hidden />
                            {fresh ? 'Provision' : 'Retry'}
                        </Button>
                    ) : null}
                    <Button
                        size="sm"
                        variant="ghost"
                        disabled={requeue.isPending}
                        data-testid="ledger-requeue"
                        onClick={() => {
                            setError(null);
                            requeue.mutate(undefined, {
                                onError,
                                onSuccess: (r) => setRequeued(r.requeued),
                            });
                        }}
                    >
                        Re-queue dead events
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="space-y-3">
                <dl className="text-muted-foreground grid grid-cols-1 gap-x-4 gap-y-1 text-xs md:grid-cols-3">
                    <div>
                        <dt className="inline">Ledger tenant </dt>
                        <dd className="text-foreground inline">
                            {ledger.ledger_tenant_id ? (
                                <PublicId id={ledger.ledger_tenant_id} />
                            ) : (
                                '—'
                            )}
                        </dd>
                    </div>
                    <div>
                        <dt className="inline">Environment </dt>
                        <dd className="text-foreground inline">
                            {ledger.environment === 'sandbox'
                                ? 'Sandbox'
                                : ledger.environment === 'live'
                                  ? 'Live'
                                  : '—'}
                        </dd>
                    </div>
                    <div>
                        <dt className="inline">API keys </dt>
                        <dd className="text-foreground inline">
                            {ledger.key_count ?? '—'}
                        </dd>
                    </div>
                </dl>
                <ul className="divide-y" data-testid="ledger-steps">
                    {steps.map((s) => (
                        <StepRow key={s.code} step={s} />
                    ))}
                </ul>
                {requeued !== null ? (
                    <p className="text-muted-foreground text-xs" role="status">
                        {requeued === 0
                            ? 'No dead events to re-queue.'
                            : `${requeued} dead event${requeued === 1 ? '' : 's'} re-queued.`}
                    </p>
                ) : null}
                {error ? (
                    <p role="alert" className="text-destructive text-xs">
                        {error}
                    </p>
                ) : null}
            </CardContent>
        </Card>
    );
}
